import React, { useEffect, useState } from "react";
import { Button, Divider, Flex, Input, Select, Radio, notification } from "antd";
import { SmallTitle } from "components/Common/Typography/Titles";
import { readUser } from "services/localStorage.service";
import { useSelector } from "react-redux";
import BulkMailMessageBox from "./BulkMailMessageBox";
import { useParams } from "react-router-dom";
import QuestionTemplate from "./QuestionTemplate";
import TemplateListDropdown from "components/Common/templateListDropdown/TemplateListDropdown";

interface BulkMailFormProps {
  subject?: any;
  setSubject?: any;
  message?: any;
  setMessage?: any;
  selectedMails?: any;
  setSelectedMails?: any;
  setFirstCandidateId?: any,
  isShowCalenderLink?: any;
  setIsShowCalenderLink?: any;
  interviewMode?: any;
  setInterviewMode?: any;
  questionId?: any;
  setQuestionId?: any;
  isVideoBased?: any;
  handleVideoBasedChange?: any;
  profileId?: any;
  emails?: any;
  selectedCcMails?: any;
  setSelectedCcMails?: any;
  setCustomSelectedMails?: any;
  setEmailTemplateId: any;
  customSelectedMails?: any;
  handleSendMail: () => void;
  isSending: boolean;
  selectedCandidates?: any[];
}

const BulkMailForm: React.FC<BulkMailFormProps> = ({
  subject,
  setSubject,
  message,
  setMessage,
  selectedMails,
  setSelectedMails,
  setFirstCandidateId,
  isShowCalenderLink,
  setIsShowCalenderLink,
  interviewMode,
  setInterviewMode,
  questionId,
  setQuestionId,
  isVideoBased,
  handleVideoBasedChange,
  profileId,
  emails,
  setEmailTemplateId,
  selectedCcMails,
  setSelectedCcMails,
  setCustomSelectedMails,
  customSelectedMails,
  handleSendMail,
  isSending
}: any) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const { projectId } = useParams();
  const [templateData, setTemplateData] = useState<any>(null);
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [tempSelectedMails, setTempSelectedMails] = useState<string[]>([]);

  const cleanEmailInput = (email: any) => {
    // Handle array case
    if (Array.isArray(email)) {
      return email.map(email =>
        typeof email === 'string' && email.endsWith('*')
          ? email.slice(0, -1)
          : email
      );
    }

    // Handle single string case
    if (typeof email === 'string') {
      return email.endsWith('*') ? email.slice(0, -1) : email;
    }

    // Return as-is for other types
    return email;
  };

  const handleSelectCcMail = (value: string) => {
    if (emailRegex.test(value)) {
      setSelectedCcMails((prevMails: any) => selectedCcMails ? Array.from(new Set([...prevMails, value])) : Array.from(new Set([value])));
    } else {
      notification.error({
        message: "Invalid Email.",
        description: "Please enter valid email address!",
      });
    }
  };

  const handleDeselectCcMail = (value: string) => {
    setSelectedCcMails(selectedCcMails.filter((mail: string) => mail !== value));
  };

  const handleSelectMail = (value: string) => {
    if (emailRegex.test(value)) {
      const validEmails = selectedCandidates.map((candidate: any) => candidate.email).filter((email: any) => email !== null)
        .flat();

      const isEmailPresent = validEmails.includes(value);
      if (isEmailPresent) {
        setSelectedMails((prevMails: any) => Array.from(new Set([...prevMails, value])));
        setTempSelectedMails((prevMails: any) => Array.from(new Set([...prevMails, value])));
      } else {
        setTempSelectedMails((prevMails: any) => Array.from(new Set([...prevMails, value])));
        setCustomSelectedMails((prevMails: any) => Array.from(new Set([...prevMails, value])));
      }
    } else {
      notification.error({
        message: "Invalid Email.",
        description: "Please enter valid email address!",
      });
    }
  };

  const handleDeselectMail = (value: string) => {
    const updatedTempSelectedMails = tempSelectedMails.filter((mail: string) => mail !== value);
    const updatedCustomSelectedMails = customSelectedMails.filter((mail: string) => mail !== value);
    const updatedSelectedMails = [...updatedTempSelectedMails, ...updatedCustomSelectedMails];
    // Set all updated states
    setTempSelectedMails(updatedTempSelectedMails);
    setCustomSelectedMails(updatedCustomSelectedMails);
    setSelectedMails(updatedSelectedMails);
    // Check against updated list, not old state
    if (updatedSelectedMails.length === 0) {
      notification.warning({
        message: "Warning",
        description: "At least one mail is required!",
      });
    }
  };

  const user = readUser();
  const selectedCandidates = useSelector((state: any) => state.candidateBulkMail.candidates) || [];
  // const selectedCandidates = [
  //   {
  //     id: 116088,
  //     name: "Jeff Fox",
  //     profile: "https://storage.googleapis.com/content-crawling-graph-images/e0/e048c748d8d7443dd75969ad0f99a47424074fd1/downloaded_profile_picture.jpg",
  //     location: "San Rafael, CA, USA",
  //     jobTitle: "Physician",
  //     graphProfileId: 28048155,
  //     email: ["foxjeff0@gmail.com"]
  //   },
  //   {
  //     id: 116128,
  //     name: "Justin Mager",
  //     profile: "https://storage.googleapis.com/content-crawling-graph-images/50/507a872c1cfae09be0a1580aa58f145c85722fa7/downloaded_profile_picture.jpg",
  //     location: "Mill Valley, CA 94941, USA",
  //     jobTitle: "Owner, Physician",
  //     graphProfileId: 28048359,
  //     email: ["justinmagermd@gmail.com"]
  //   }
  // ];

  const getEmailsByGraphId = (graphProfileId: number, selectedCandidates: any[], selectedMails: string[]) => {
    const candidate = selectedCandidates.find(c => c.graphProfileId === graphProfileId);
    if (!candidate) return [];

    return candidate.email.filter((email: any) => selectedMails.includes(email)) || null;
  };


  useEffect(() => {
    const all_emails = selectedCandidates.find((candidate: any) => candidate.graphProfileId === profileId) as any;
    if (all_emails && all_emails.email.length > 0) {
      setCandidateOptions(
        all_emails.email
          .filter((email: any) => !!email)
          .map((email: any) => {
            if (typeof email === 'string') {
              const cleaned = email.replace(/\*$/, '');
              return { value: cleaned, label: cleaned };
            } else if (typeof email === 'object' && email.value) {
              const cleanedValue = email.value.replace(/\*$/, '');
              const cleanedLabel = email.label.replace(/\*$/, '');
              return { value: cleanedValue, label: cleanedLabel };
            }
            return null;
          })
          .filter(Boolean)
      );
    }

    if (selectedCandidates.length > 0) {
      const firsCandidate = selectedCandidates[0];
      setFirstCandidateId(firsCandidate?.graphProfileId);
      if (selectedCandidates && selectedCandidates.length > 0) {

        const validEmails = selectedCandidates.map((candidate: any) => candidate.email).filter((email: any) => email !== null)
          .flat();
        setSelectedMails(validEmails);
      }
    }
  }, [selectedCandidates]);

  useEffect(() => {
    if (selectedCandidates && selectedCandidates.length > 0) {
      // Filter candidates with valid graphProfileId and at least one email
      const candidatesWithEmailAndProfileId = selectedCandidates.filter(
        (candidate: any) => candidate.graphProfileId === profileId && candidate.email?.length > 0
      );

      // Extract and flatten valid emails
      const validEmails = candidatesWithEmailAndProfileId
        .map((candidate: any) => candidate.email)
        .flat();
      setTempSelectedMails(cleanEmailInput(validEmails));
    }
  }, [profileId]);

  useEffect(() => {
    const allSelected = candidateOptions.map((opt: { value: string; label: string }) => opt.value);
    const uniqueSelected = Array.from(new Set(allSelected));
  }, [candidateOptions]);


  useEffect(() => {
    setSelectedCcMails(templateData?.cc_emails);
  }, [templateData]);

  const options = [
    { label: "Video", value: true },
    { label: "Text", value: false },
  ];
  if (!profileId) {
    return null;
  }
  return (
    <div className="px-8">
      <Flex gap={32} align="center">
        <div className="min-w-[200px] max-w-[280px] flex gap-1">
          <SmallTitle text="From" className="text-text-secondary font-semibold" />
          <span className="text-primary">*</span>
        </div>
        <Flex gap={24} className="w-full">
          <Input size="large" value={`${user.first_name} ${user.last_name} (you)`} />
          <Input size="large" value={user.email} />
        </Flex>
      </Flex>
      <Divider className="my-4" />
      <Flex gap={32} align="center">
        <div className="min-w-[200px] max-w-[280px] flex gap-1">
          <SmallTitle text="To" className="text-text-secondary font-semibold" />
          <span className="text-primary">*</span>
        </div>
        <Flex gap={24} className="flex-1">
          <Select
            mode="tags"
            allowClear
            style={{ width: "100%" }}
            placeholder="Please select"
            onSelect={handleSelectMail}
            onDeselect={handleDeselectMail}
            value={tempSelectedMails}
            options={Array.from(
              new Map(
                (candidateOptions as { value: string; label: string }[]).map((opt) => [
                  opt.value.replace(/\*$/, ""),
                  {
                    value: opt.value.replace(/\*$/, ""),
                    label: opt.label.replace(/\*$/, ""),
                  },
                ])
              ).values()
            )}
            className="flex-1"
            size="large"
          />
        </Flex>
      </Flex>
      {templateData && (
        <>
          <Divider className="my-4" />
          <Flex gap={32} align="center">
            <div className="min-w-[200px] max-w-[280px] flex gap-1">
              <SmallTitle text="Cc" className="text-text-secondary font-semibold" />
            </div>
            <Flex gap={24} className="flex-1">
              <Select
                mode="tags"
                allowClear
                style={{ width: "100%" }}
                placeholder="Please select"
                onSelect={handleSelectCcMail}
                onDeselect={handleDeselectCcMail}
                value={selectedCcMails}
                className="flex-1"
                size="large"
              />
            </Flex>
          </Flex>
        </>
      )}

      <Divider className="my-4" />
      <Flex gap={32} align="center">
        <div className="min-w-[200px] max-w-[280px] flex gap-1">
          <SmallTitle text="Email Template" className="text-text-secondary font-semibold" />
        </div>
        <Flex gap={24} className="flex-1">
          <TemplateListDropdown
            // disabled={true}
            size="large"
            style={{ width: "100%", background: "#fff" }}
            placeholder="Please select template"
            options={options}
            onChange={(templateId: any, templateDetails: any) => {
              setTemplateData({
                ...templateDetails.details,
                emailSignature: templateDetails.details.signature,
              });
              setEmailTemplateId(templateDetails.details.id);
              //setRefresh(true);
              //onTemplateUpdate && onTemplateUpdate(profileData.id, templateId);

              //onEmailAddressUpdate(profileData.id, emails);
            }}
            type="BULK_EMAIL_TEMPLATE"
          />
        </Flex>
      </Flex>
      <Divider className="my-4" />
      <Flex gap={32} align="center">
        <div className="min-w-[200px] max-w-[280px] flex gap-1">
          <SmallTitle text="Subject" className="text-text-secondary font-semibold" />
        </div>
        <Flex gap={24} className="flex-1">
          <Input
            size="large"
            placeholder="Product Designer"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </Flex>
      </Flex>

      <Divider className="my-4" />
      <Flex gap={32} align="center">
        <div className="min-w-[200px] max-w-[280px] flex flex-col gap-1">
          <SmallTitle text="Interview Mode" className="text-text-secondary font-semibold" />
        </div>
        <Flex gap={4} className="flex-1" vertical>
          <Select
            id="interviewModeTemplate"
            placeholder="Select interview mode"
            options={[{ value: "screening", label: "Screening" }]}
            style={{ width: "100%" }}
            size="large"
            value={interviewMode}
            onChange={(value) => setInterviewMode(value)}
            allowClear
          />
          {interviewMode === "screening" && (
            <div className="my-3">
              <Radio.Group
                options={options}
                value={isVideoBased}
                onChange={handleVideoBasedChange}
              />
            </div>
          )}
        </Flex>
      </Flex>

      <Divider className="my-4" />
      {interviewMode === "screening" ? (
        <>
          <Flex gap={32} align="center">
            <div className="min-w-[200px] max-w-[280px] flex gap-1">
              <SmallTitle
                text="Select prescreen questions"
                className="text-text-secondary font-semibold"
              />
              <span className="text-primary">*</span>
            </div>
            <Flex gap={24} className="flex-1" vertical>
              <QuestionTemplate questionId={questionId} setQuestionId={setQuestionId} />
            </Flex>
          </Flex>
          <Divider className="my-4" />
        </>
      ) : (
        <>
          {/* <Flex gap={32} align="center">
                        <div className="min-w-[200px] max-w-[280px] flex gap-1">
                            <SmallTitle text="Calender" className="text-text-secondary font-semibold" />
                            {SvgIcons.questionSvg}
                        </div>
                        <Flex gap={24} className="flex-1" vertical>
                            <Button
                                onClick={() => setIsShowCalenderLink(!isShowCalenderLink)}
                                className={`h-8 w-fit py-[10px] flex px-[14px] rounded-lg ${isShowCalenderLink ? "text-white" : "text-text-secondary border"}`}
                                type={isShowCalenderLink ? "primary" : "default"}
                                style={{
                                    border: "8px",

                                }}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.99984 4.16602V15.8327M4.1665 9.99935H15.8332" stroke={isShowCalenderLink ? "#fff" : "#344054"} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Add Link
                            </Button>

                        </Flex>
                    </Flex>
                    <Divider className="my-4" /> */}
        </>
      )}

      <Flex gap={32} align="center">
        <div className="min-w-[200px] max-w-[280px] flex flex-col">
          <Flex gap={4}>
            <SmallTitle text="Message" className="text-text-secondary font-semibold" />
            <span className="text-primary">*</span>
          </Flex>
          <SmallTitle text="Write a short message" className="text-text-tertiary font-normal" />
        </div>
        {profileId && (
          <Flex gap={24} className="flex-1">
            <BulkMailMessageBox
              setMessage={setMessage}
              message={message}
              setSubject={setSubject}
              projectId={projectId}
              profileId={profileId}
              templateData={templateData}
              candidates={selectedCandidates}
            />
          </Flex>
        )}
      </Flex>

      <Flex gap={32} align="center">
        <div className="w-full py-4 text-right">
          <Button
            className="text-white py-2 px-3 bg-[#845dda] border border-[#7F56D9]"
            onClick={handleSendMail}
            loading={isSending}
            icon={
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.74928 11.2501L17.4993 2.50014M8.85559 11.5235L11.0457 17.1552C11.2386 17.6513 11.3351 17.8994 11.4741 17.9718C11.5946 18.0346 11.7381 18.0347 11.8587 17.972C11.9978 17.8998 12.0946 17.6518 12.2881 17.1559L17.78 3.08281C17.9547 2.63516 18.0421 2.41133 17.9943 2.26831C17.9528 2.1441 17.8553 2.04663 17.7311 2.00514C17.5881 1.95736 17.3643 2.0447 16.9166 2.21939L2.84349 7.71134C2.34759 7.90486 2.09965 8.00163 2.02739 8.14071C1.96475 8.26129 1.96483 8.40483 2.02761 8.52533C2.10004 8.66433 2.3481 8.7608 2.84422 8.95373L8.47589 11.1438C8.5766 11.183 8.62695 11.2026 8.66935 11.2328C8.70693 11.2596 8.7398 11.2925 8.7666 11.3301C8.79685 11.3725 8.81643 11.4228 8.85559 11.5235Z"
                  stroke="currentColor"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            disabled={!((tempSelectedMails?.length || 0) + (customSelectedMails?.length || 0))}
          >
            Send Email
          </Button>
        </div>
      </Flex>
    </div>
  );
};

export default BulkMailForm;
